import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useSWR from 'swr';
import { Star, Loader2, CheckCircle, AlertCircle, ArrowLeft, LogIn, User, Edit2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const fetcher = (url) => fetch(url, { credentials: 'include' }).then(res => {
  if (!res.ok) throw new Error('Failed to fetch');
  return res.json();
});

/**
 * CustomerReviewPage - Auth-walled page for customers to leave or edit reviews
 * URL: /m/{orgSlug}/review or /{orgSlug}/review
 * 
 * Requires customer to be signed in via Google OAuth.
 * Google-style rule: 1 review per customer per operator.
 * If customer already has a review, shows edit mode.
 */
const CustomerReviewPage = () => {
  const { orgSlug } = useParams();
  const navigate = useNavigate();
  const { customer, loading: authLoading, isAuthenticated, loginWithGoogle } = useCustomerAuth();
  
  // Step state: 'select' -> 'write' -> 'success'
  const [step, setStep] = useState('select');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Review form
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');

  // Fetch eligible bookings for this operator (only when authenticated)
  const { data: eligibleData, error: eligibleError, isLoading: eligibleLoading } = useSWR(
    isAuthenticated && orgSlug ? `${API_URL}/api/customer/reviews/eligible/${orgSlug}` : null,
    fetcher
  );

  // Pre-populate form if customer has existing review (Google-style editing)
  useEffect(() => {
    if (eligibleData?.existing_review) {
      const existing = eligibleData.existing_review;
      setRating(existing.rating);
      setTitle(existing.title || '');
      setComment(existing.comment || '');
      setIsEditing(true);
      // Auto-select first booking if editing
      if (eligibleData.bookings?.length > 0) {
        setSelectedBooking(eligibleData.bookings[0]);
        setStep('write');
      }
    }
  }, [eligibleData]);

  // Handle login redirect - save current URL to return after auth
  const handleLogin = () => {
    sessionStorage.setItem('reviewRedirect', window.location.pathname);
    loginWithGoogle();
  };

  // Submit the review
  const submitReview = async (e) => {
    e.preventDefault();
    if (!selectedBooking || rating === 0) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/customer/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          booking_id: selectedBooking.id,
          rating,
          title: title.trim() || null,
          comment: comment.trim() || null
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setStep('success');
        toast.success(data.updated ? 'Review updated!' : 'Thank you for your review!');
      } else {
        toast.error(data.detail || 'Could not submit review');
      }
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Format date nicely
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Auth loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Not authenticated - show login prompt
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-16">
              <Link to="/" className="flex items-center gap-2">
                <img src="/logo-icon.png" alt="BookingFlows" className="h-9 w-9 object-contain rounded-lg" />
                <span className="text-xl font-bold text-gray-900">BookingFlows</span>
              </Link>
            </div>
          </div>
        </header>

        <div className="max-w-md mx-auto px-4 py-16">
          <Card className="rounded-3xl">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-6">
                <Star className="w-8 h-8 text-blue-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-3">Leave a Review</h1>
              <p className="text-gray-500 mb-6">
                Sign in to leave a review for your recent trip
              </p>
              <Button 
                onClick={handleLogin}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white py-6 text-lg rounded-full"
                data-testid="login-to-review-btn"
              >
                <LogIn className="w-5 h-5 mr-2" />
                Sign in with Google
              </Button>
              <p className="text-sm text-gray-400 mt-4">
                We use your account to verify your bookings
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Loading eligible bookings
  if (eligibleLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Error state - operator not found or fetch failed
  if (eligibleError || !eligibleData?.operator) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h1>
          <p className="text-gray-500 mb-6">We couldn't load this page. Please try again.</p>
          <Button onClick={() => navigate('/')} variant="outline" className="rounded-full">
            Go to Marketplace
          </Button>
        </div>
      </div>
    );
  }

  const operator = eligibleData.operator;
  const eligibleBookings = eligibleData.bookings || [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate('/my-trips')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Back to My Trips</span>
            </button>
            
            {/* User avatar */}
            {customer && (
              <div className="flex items-center gap-2">
                {customer.avatar_url ? (
                  <img src={customer.avatar_url} alt={customer.name} className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                    <User className="w-4 h-4 text-gray-500" />
                  </div>
                )}
                <span className="text-sm text-gray-600 hidden sm:inline">{customer.name}</span>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8">
        
        {/* Operator Branding */}
        <div className="text-center mb-8">
          {operator.logo_url ? (
            <img 
              src={operator.logo_url} 
              alt={operator.name}
              className="w-20 h-20 rounded-2xl mx-auto mb-4 object-contain bg-white p-2 shadow-sm"
            />
          ) : (
            <div className="w-20 h-20 rounded-2xl mx-auto mb-4 bg-gray-900 flex items-center justify-center text-white text-2xl font-bold">
              {operator.name?.charAt(0) || '?'}
            </div>
          )}
          <h1 className="text-2xl font-bold text-gray-900">{operator.name}</h1>
          <p className="text-gray-500 mt-1">Share your experience</p>
        </div>

        {/* No eligible bookings */}
        {step === 'select' && eligibleBookings.length === 0 && (
          <Card className="rounded-3xl">
            <CardContent className="py-8 text-center">
              <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">No Trips to Review</h2>
              <p className="text-gray-500 mb-4">
                You don't have any recent trips with {operator.name} that are eligible for review.
              </p>
              <p className="text-sm text-gray-400 mb-6">
                Reviews can be submitted within 90 days after your trip.
              </p>
              <Button 
                onClick={() => navigate(`/host/${orgSlug}`)}
                variant="outline"
                className="rounded-full"
              >
                Browse Experiences
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step: Select Booking */}
        {step === 'select' && eligibleBookings.length > 0 && (
          <Card className="rounded-3xl">
            <CardContent className="py-6">
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Select a Trip to Review</h2>
                <p className="text-gray-500 mt-1">{eligibleBookings.length} eligible booking{eligibleBookings.length !== 1 ? 's' : ''}</p>
              </div>
              
              <div className="space-y-2">
                {eligibleBookings.map((booking) => (
                  <button
                    key={booking.id}
                    onClick={() => {
                      setSelectedBooking(booking);
                      setStep('write');
                    }}
                    className="w-full p-4 rounded-2xl text-left hover:bg-gray-50 transition-all group"
                    data-testid={`booking-option-${booking.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
                        {booking.service_image ? (
                          <img 
                            src={booking.service_image} 
                            alt={booking.service_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-blue-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                          {booking.service_name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatDate(booking.booking_date)}
                        </p>
                      </div>
                      <Star className="w-5 h-5 text-gray-300 group-hover:text-amber-400 transition-colors flex-shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step: Write Review */}
        {step === 'write' && selectedBooking && (
          <Card className="rounded-3xl">
            <CardContent className="py-6">
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {isEditing ? 'Edit Your Review' : 'Write Your Review'}
                </h2>
                <p className="text-gray-500 mt-1">{selectedBooking.service_name}</p>
                {isEditing && (
                  <p className="text-sm text-blue-600 mt-2 flex items-center justify-center gap-1">
                    <Edit2 className="w-3 h-3" />
                    Updating your existing review
                  </p>
                )}
              </div>
              
              <form onSubmit={submitReview} className="space-y-6">
                {/* Star Rating */}
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-700 mb-3">How was your experience?</p>
                  <div className="flex justify-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="p-1 transition-transform hover:scale-110"
                        data-testid={`star-${star}`}
                      >
                        <Star 
                          className={`w-10 h-10 transition-colors ${
                            star <= (hoverRating || rating) 
                              ? 'text-amber-400 fill-amber-400' 
                              : 'text-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                  {rating > 0 && (
                    <p className="text-sm text-amber-600 mt-2 font-medium">
                      {rating === 5 ? 'Excellent!' : rating === 4 ? 'Great!' : rating === 3 ? 'Good' : rating === 2 ? 'Fair' : 'Poor'}
                    </p>
                  )}
                </div>
                
                {/* Title (optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title <span className="text-gray-400">(optional)</span>
                  </label>
                  <Input
                    placeholder="Summarize your experience"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={100}
                    className="rounded-xl"
                    data-testid="review-title-input"
                  />
                </div>
                
                {/* Comment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Review <span className="text-gray-400">(optional)</span>
                  </label>
                  <Textarea
                    placeholder="Tell others about your experience..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={4}
                    maxLength={1000}
                    className="rounded-xl"
                    data-testid="review-comment-input"
                  />
                </div>
                
                {/* Submit */}
                <Button 
                  type="submit" 
                  disabled={submitting || rating === 0}
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white py-6 text-lg disabled:opacity-50 rounded-full"
                  data-testid="submit-review-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {isEditing ? 'Updating...' : 'Submitting...'}
                    </>
                  ) : (
                    isEditing ? 'Update Review' : 'Submit Review'
                  )}
                </Button>
              </form>
              
              {!isEditing && (
                <button 
                  onClick={() => {
                    setStep('select');
                    setRating(0);
                    setTitle('');
                    setComment('');
                  }}
                  className="w-full mt-4 text-sm text-gray-500 hover:text-gray-700"
                >
                  ← Choose a different booking
                </button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <Card className="rounded-3xl">
            <CardContent className="py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {isEditing ? 'Review Updated!' : 'Thank You!'}
              </h2>
              <p className="text-gray-500 mb-6">
                Your review helps {operator.name} and future customers.
              </p>
              <div className="flex justify-center gap-2 mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star 
                    key={star}
                    className={`w-6 h-6 ${star <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
                  />
                ))}
              </div>
              <Button 
                onClick={() => navigate('/my-trips')}
                variant="outline"
                className="rounded-full"
                data-testid="view-trips-btn"
              >
                Back to My Trips
              </Button>
            </CardContent>
          </Card>
        )}

      </div>
    </div>
  );
};

export default CustomerReviewPage;
